/* =============================================================================
 * AYA CountUp — H_UIPOLISH_V1_L3 Palier 1
 * -----------------------------------------------------------------------------
 * Animation de compteur numérique via framer-motion (useMotionValue +
 * useTransform + animate). Aucun setInterval custom.
 *
 * Props:
 *   - value (number, required)        : valeur cible
 *   - duration (number, ms, default 800)
 *   - decimals (number, default 0)
 *   - prefix (string)                 : ex "€ " ou "+"
 *   - suffix (string)                 : ex " FCFA" ou " %"
 *   - locale (string, default fr-FR)  : pour le séparateur de milliers
 *   - ease (string|function)          : easing framer-motion
 *   - aria-label (string)             : label accessible (sinon valeur finale)
 *
 * Respect prefers-reduced-motion : affiche directement la valeur finale.
 * ========================================================================== */

import React, { useEffect, useRef, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";

function formatNumber(value, decimals, locale) {
    try {
        return Number(value).toLocaleString(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    } catch (_e) {
        return Number(value).toFixed(decimals);
    }
}

export function AyaCountUp({
    value = 0,
    duration = 800,
    decimals = 0,
    prefix = "",
    suffix = "",
    locale = "fr-FR",
    ease = [0.32, 0.72, 0, 1],
    className,
    style,
    "aria-label": ariaLabel,
    ...rest
}) {
    const reduce = useReducedMotion();
    const [display, setDisplay] = useState(() =>
        formatNumber(reduce ? value : 0, decimals, locale)
    );
    const prevRef = useRef(reduce ? value : 0);

    useEffect(() => {
        const target = Number(value) || 0;

        if (reduce) {
            prevRef.current = target;
            setDisplay(formatNumber(target, decimals, locale));
            return undefined;
        }

        const controls = animate(prevRef.current, target, {
            duration: duration / 1000,
            ease,
            onUpdate: (latest) => {
                setDisplay(formatNumber(latest, decimals, locale));
            },
            onComplete: () => {
                prevRef.current = target;
                setDisplay(formatNumber(target, decimals, locale));
            },
        });

        return () => controls.stop();
    }, [value, duration, decimals, locale, ease, reduce]);

    const finalLabel =
        ariaLabel ||
        `${prefix}${formatNumber(value, decimals, locale)}${suffix}`;

    return (
        <span
            data-testid="aya-countup"
            className={className}
            style={{
                display: "inline-block",
                fontVariantNumeric: "tabular-nums",
                ...style,
            }}
            aria-label={finalLabel}
            role="text"
            {...rest}
        >
            {prefix}
            {display}
            {suffix}
        </span>
    );
}

export default AyaCountUp;
